import React, { useState } from "react";
import {
  IconFileText,
  IconFileTypePdf,
  IconFileSpreadsheet,
  IconFileTypePpt,
  IconPhoto,
  IconDownload,
  IconEye,
  IconChevronDown,
  IconChevronUp,
  IconLoader,
} from "@tabler/icons-react";

const DownloadFile = ({ instructions }) => {
  const [expanded, setExpanded] = useState(true);
  const [downloadingFiles, setDownloadingFiles] = useState({});

  const documents = Array.isArray(instructions?.documents)
    ? instructions.documents
    : [];

  const getDocumentName = (docPath) => {
    if (!docPath || typeof docPath !== "string") return "Unknown Document";

    try {
      // Extract filename from path and remove any unnecessary prefixes
      const filename = docPath.split("/").pop().split("\\").pop();

      // If filename contains underscores, try to clean it up
      if (filename.includes("_")) {
        const parts = filename.split("_");
        // Check if first part looks like a prefix we should remove
        if (parts.length > 1 && !parts[0].includes(".")) {
          return parts.slice(1).join("_");
        }
      }

      return filename;
    } catch (error) {
      console.error("Error processing document name:", error);
      return String(docPath);
    }
  };

  // Function to get file icon based on file extension
  const getFileTypeIcon = (docPath) => {
    if (!docPath) return <IconFileText size={28} />;

    const extension = docPath.split(".").pop().toLowerCase();

    switch (extension) {
      case "pdf":
        return <IconFileTypePdf size={28} className="text-red-500" />;
      case "doc":
      case "docx":
        return <IconFileText size={28} className="text-blue-600" />;
      case "xls":
      case "xlsx":
        return <IconFileSpreadsheet size={28} className="text-green-600" />;
      case "ppt":
      case "pptx":
        return <IconFileTypePpt size={28} className="text-orange-500" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <IconPhoto size={28} className="text-purple-500" />;
      default:
        return <IconFileText size={28} className="text-gray-500" />;
    }
  };

  const handleDownloadAll = async () => {
    if (documents.length === 0) {
      alert("No documents available for download.");
      return;
    }

    // Set all documents as downloading
    const downloadingState = {};
    documents.forEach((doc, index) => {
      downloadingState[index] = true;
    });
    setDownloadingFiles(downloadingState);

    try {
      // Show global downloading toast
      const toast = createToast("Downloading all files...", "info");

      for (let i = 0; i < documents.length; i++) {
        await downloadFile(documents[i], i);
      }

      // Remove global toast and show success
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
      createToast("All files downloaded successfully", "success", 3000);
    } catch (error) {
      createToast(`Error: ${error.message}`, "error", 5000);
    } finally {
      // Reset all downloading states
      setDownloadingFiles({});
    }
  };

  const createToast = (message, type = "info", duration = null) => {
    // Remove any existing toasts
    const existingToasts = document.querySelectorAll(".custom-toast");
    existingToasts.forEach((toast) => document.body.removeChild(toast));

    // Create new toast
    const toast = document.createElement("div");
    toast.className = "custom-toast";
    toast.textContent = message;

    // Set style based on type
    let bgColor = "#4299e1"; // Default blue for info
    if (type === "success") bgColor = "#48bb78";
    if (type === "error") bgColor = "#f56565";

    toast.style.cssText = `
      position: fixed; 
      bottom: 20px; 
      right: 20px; 
      background: ${bgColor}; 
      color: white; 
      padding: 12px 20px; 
      border-radius: 6px; 
      z-index: 1000; 
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease-out forwards;
    `;

    // Add keyframes animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(toast);

    // Auto-remove toast after duration if specified
    if (duration) {
      setTimeout(() => {
        if (document.body.contains(toast)) {
          toast.style.animation = "slideOut 0.3s ease-in forwards";
          setTimeout(() => {
            if (document.body.contains(toast)) {
              document.body.removeChild(toast);
            }
          }, 300);
        }
      }, duration);
    }

    return toast;
  };

  const downloadFile = async (doc, index = null) => {
    // Set this specific file as downloading if index provided
    if (index !== null) {
      setDownloadingFiles((prev) => ({ ...prev, [index]: true }));
    }

    try {
      // Ensure the URL is constructed correctly with the /media/ path
      const fileUrl = doc.startsWith("/")
        ? `${process.env.REACT_APP_BASE_URL}${doc}`
        : `${process.env.REACT_APP_BASE_URL}/media/${doc}`;

      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(
          `Failed to download file: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = getDocumentName(doc);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Show success toast only for individual downloads
      if (index !== null && !downloadingFiles[-1]) {
        // Don't show for batch downloads
        createToast(`Downloaded ${filename}`, "success", 3000);
      }
    } catch (error) {
      console.error("Error downloading file:", error);

      // Show error toast
      createToast(`Error: ${error.message}`, "error", 5000);

      // Re-throw to handle in the calling function
      throw error;
    } finally {
      // Clear downloading state for this file
      if (index !== null) {
        setDownloadingFiles((prev) => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    }
  };

  // Get file extension for display
  const getFileExtension = (filename) => {
    if (!filename) return "";
    const extension = filename.split(".").pop().toLowerCase();
    return extension ? `.${extension}` : "";
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden transition-all duration-300">
      {/* Header with expand/collapse functionality */}
      <div
        className="flex justify-between items-center p-4 border-b border-gray-100 cursor-pointer bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center">
          <IconFileText
            className="text-purple-600 mr-2"
            size={22}
            stroke={1.5}
          />
          <h2 className="text-lg font-bold text-gray-800">
            Important Documents
          </h2>
          <span className="ml-2 bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-full">
            {documents.length}
          </span>
        </div>

        <div className="flex items-center">
          {documents.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent toggling expand/collapse
                handleDownloadAll();
              }}
              className="mr-4 bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-1.5 rounded-md transition-colors flex items-center"
              disabled={Object.keys(downloadingFiles).length > 0}
            >
              {Object.keys(downloadingFiles).length > 0 ? (
                <>
                  <IconLoader className="animate-spin mr-1" size={16} />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <IconDownload size={16} className="mr-1" />
                  <span>Download All</span>
                </>
              )}
            </button>
          )}

          <div className="text-gray-500 hover:text-purple-600 transition-colors">
            {expanded ? (
              <IconChevronUp size={20} stroke={1.5} />
            ) : (
              <IconChevronDown size={20} stroke={1.5} />
            )}
          </div>
        </div>
      </div>

      {/* Document list with smooth collapse/expand animation */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          expanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4">
            <IconFileText size={48} className="text-gray-300 mb-3" />
            <p className="text-gray-500 text-center">
              No documents available for this visa application.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {documents.map((doc, index) => {
              const fileName = getDocumentName(doc);
              const fileExt = getFileExtension(fileName);
              const nameWithoutExt = fileName.replace(fileExt, "");
              const isDownloading = downloadingFiles[index];

              return (
                <div
                  key={index}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col"
                >
                  <div className="p-4 bg-gray-50 border-b border-gray-100 flex items-center">
                    {getFileTypeIcon(doc)}
                    <div className="ml-3 overflow-hidden">
                      <p
                        className="text-sm font-medium text-gray-700 truncate"
                        title={nameWithoutExt}
                      >
                        {nameWithoutExt}
                      </p>
                      <p className="text-xs text-gray-500 uppercase">
                        {fileExt.substring(1)}
                      </p>
                    </div>
                  </div>

                  <div className="p-3 flex justify-between items-center">
                    <a
                      href={
                        doc.startsWith("/")
                          ? `${process.env.REACT_APP_BASE_URL}${doc}`
                          : `${process.env.REACT_APP_BASE_URL}/media/${doc}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        const url = doc.startsWith("/")
                          ? `${process.env.REACT_APP_BASE_URL}${doc}`
                          : `${process.env.REACT_APP_BASE_URL}/media/${doc}`;
                        window.open(url, "_blank");
                      }}
                    >
                      <IconEye size={16} className="mr-1" />
                      <span>View</span>
                    </a>

                    <button
                      className={`text-sm flex items-center ${
                        isDownloading
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:text-blue-800"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isDownloading) {
                          downloadFile(doc, index);
                        }
                      }}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <>
                          <IconLoader size={16} className="animate-spin mr-1" />
                          <span>Downloading...</span>
                        </>
                      ) : (
                        <>
                          <IconDownload size={16} className="mr-1" />
                          <span>Download</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadFile;
